import type { Survey } from 'types';
import { NavLink } from '@/components/NavLink';
import { useNavigate } from 'react-router';
import { DataTable, useDataControl } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/SearchInput';
import { UserAvatar } from '@/components/UserAvatar';
import { formatDateTime } from 'helpers/formatters';
import { cn } from 'helpers/cn';
import * as Icons from 'lucide-react';
import { useSurveysList } from '@/state/surveys/useSurveysList';
import { FOCUS_RING, SEARCH_FIELD_CLASS } from '../../ConsolePanel';
import { TRIGGER_LABEL } from '../survey-copy';
import { SurveyRowActions } from './SurveyRowActions';
import { SurveysEmptyState } from './SurveysEmptyState';

const { Plus, ExternalLink, Play, Pencil, Square } = Icons;

/**
 * Console status semantics: Running is live (ok), Draft is a selection-in-progress
 * (sel), Ended is neither — a plain hairline chip rather than a colour that would
 * imply something is wrong with it.
 */
const STATUS_CHIP: Record<Survey['status'], { icon: React.ComponentType<{ className?: string }>; chip: string }> = {
  Running: { icon: Play, chip: 'border-ok-line text-ok' },
  Draft: { icon: Pencil, chip: 'border-line text-dim' },
  Ended: { icon: Square, chip: 'border-line text-dim' },
};

export const SurveysLibrary = () => {
  const navigate = useNavigate();
  const manager = useSurveysList();
  const { view, setView, views, surveys, counts, isLoading, hasWebsite } = manager;
  const dataController = useDataControl<Survey>(surveys);
  const { searchQuery, setSearchQuery } = dataController;

  const filters = views.map((value) => ({
    value,
    label: value === 'All' ? `All (${counts.all})` : value === 'Running' ? `Running (${counts.running})` : value,
  }));

  /*
    Three states came through here as one before, all of them rendering the table's
    "No surveys yet" line: no website picked (the query never ran), the query still
    in flight, and a website that genuinely has none. Only the third is an empty
    library, and only it should get the explainer.
  */
  if (!hasWebsite)
    return (
      <main className="bg-panel border-line rounded-panel border px-6 py-12 text-center">
        <p className="supporting m-0">Select a website first.</p>
      </main>
    );

  if (isLoading)
    return (
      <main className="bg-panel border-line rounded-panel border px-6 py-12 text-center">
        <p className="supporting m-0">Loading surveys…</p>
      </main>
    );

  if (counts.total === 0) return <SurveysEmptyState />;

  return (
    <main className="flex flex-col gap-[14px]">
      <section className="bg-panel border-line rounded-panel border">
        {/* Stacked rows below `md`: the pill strip takes the whole width of a phone on
            its own, so search and the create button each get a row under it. */}
        <div className="border-line flex flex-col gap-2 border-b px-4 py-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-3">
          {/*
            Pills rather than the shared segmented Tabs: the console reads filters as
            chips, and the row has to survive a narrow viewport, so it scrolls sideways
            instead of collapsing into a select.
          */}
          <div
            className="-mx-1 flex min-w-0 items-center gap-1.5 overflow-x-auto px-1 py-0.5"
            data-tour="surveys-library-views"
          >
            {filters.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setView(filter.value)}
                className={cn(
                  // border-input on an unselected pill — it has no fill, so the outline
                  // is all there is. min-h-9 clears the 36px touch floor on a phone.
                  'micro-label rounded-pill flex min-h-9 shrink-0 items-center border px-3 py-1.5',
                  'transition-colors duration-150 md:min-h-0',
                  FOCUS_RING,
                  filter.value === view
                    ? 'bg-sel-bg border-sel-line text-foreground'
                    : 'border-input text-dim hover:bg-hover hover:text-foreground',
                )}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-2 md:shrink-0 md:flex-row md:items-center">
            {/* SearchInput collapses to a magnifier below `md` and drops its field in
                absolutely, so it needs a positioned parent of the field's width. */}
            <div className="relative w-full md:w-auto" data-tour="surveys-library-search">
              <SearchInput value={searchQuery} handleSearch={setSearchQuery} className={SEARCH_FIELD_CLASS} />
            </div>
            <Button
              onClick={() => navigate('/surveys/new')}
              className="h-9 w-full rounded-lg md:w-auto"
              data-tour="surveys-library-new"
            >
              <Plus className="h-4 w-4" />
              New survey
            </Button>
          </div>
        </div>

        <div className="p-3" data-tour="surveys-library-table">
          <DataTable<Survey>
            dataController={dataController}
            pageSize={10}
            searchKeys={['name']}
            // Reaching this means the view excluded everything, not an empty library —
            // SurveysEmptyState owns that case, so the wording no longer claims there
            // are no surveys. A search that matches nothing is DataTable's own message.
            noDataMessage={
              view === 'All'
                ? 'No surveys in this view. Every survey on this website is archived.'
                : `No ${view.toLowerCase()} surveys.`
            }
            columns={[
              {
                field: 'name',
                label: 'Survey',
                sortKey: 'name',
                headerClassName: 'micro-label',
                cellClassName: 'min-w-0',
                format: ({ item }) => (
                  // hover:underline on the link, not group-hover on the row. The row
                  // itself has no onClick, so underlining the name from anywhere across
                  // Type / Trigger / Status promised a whole-row link that is not there.
                  <NavLink
                    to={`/surveys/${item.id}`}
                    title={item.name}
                    className="text-foreground flex min-w-0 items-center gap-2 font-medium hover:underline"
                  >
                    <span className="truncate">{item.name}</span>
                    <ExternalLink className="text-dim h-3.5 w-3.5 flex-shrink-0" />
                  </NavLink>
                ),
              },
              {
                field: 'type',
                label: 'Type',
                sortKey: 'type',
                headerClassName: 'micro-label w-24',
                format: ({ value }) => (
                  <span className="micro-label border-line rounded-pill inline-flex border px-2 py-0.5">
                    {value as string}
                  </span>
                ),
              },
              {
                field: 'triggerType',
                label: 'Trigger',
                sortKey: 'triggerType',
                headerClassName: 'micro-label w-36',
                format: ({ value }) => (
                  <span className="text-dim">{TRIGGER_LABEL[value as string] ?? (value as string)}</span>
                ),
              },
              {
                field: 'status',
                label: 'Status',
                sortKey: 'status',
                headerClassName: 'micro-label w-28',
                format: ({ item }) => {
                  const { icon: Icon, chip } = STATUS_CHIP[item.status];
                  return (
                    <span
                      className={cn('micro-label rounded-pill inline-flex items-center gap-1.5 border px-2 py-1', chip)}
                    >
                      <Icon className="h-3 w-3" />
                      {item.status}
                    </span>
                  );
                },
              },
              {
                field: 'createdAt',
                label: 'Created',
                sortKey: 'createdAt',
                headerClassName: 'micro-label w-32',
                cellClassName: 'num text-dim',
                format: ({ value }) => formatDateTime(value as number) || '-',
              },
              {
                field: 'createdByUser',
                label: 'Created by',
                headerClassName: 'micro-label w-20',
                mobile: 'hide',
                // The badge stands in for the name, as in the experiment library; hovering
                // it gives the full name. No sortKey: sorting on an object only moves the
                // rows without a creator to the end.
                format: ({ item }) =>
                  item.createdByUser ? <UserAvatar user={item.createdByUser} size="sm" tooltip /> : '-',
              },
              {
                label: 'Actions',
                headerClassName: 'micro-label w-24',
                cellClassName: 'w-24',
                format: ({ item, idx }) => (
                  <SurveyRowActions survey={item} tourAnchor={idx === 0 ? 'surveys-library-row-actions' : undefined} />
                ),
              },
            ]}
          />
        </div>
      </section>
    </main>
  );
};
